import React from 'react';
import { EventCard } from '../types';
import './EarningsEventCard.css';

interface EarningsEventCardProps {
  event: EventCard;
  onCardClick: (event: EventCard) => void;
  marketTiming: 'pre' | 'post';
}

const EarningsEventCard: React.FC<EarningsEventCardProps> = ({ event, onCardClick, marketTiming }) => {
  // Extract ticker symbol from title (e.g., "Will Boeing (BA) beat quarterly earnings?" -> "BA")
  const extractTicker = (title: string): string => {
    const match = title.match(/\(([A-Z]{1,5})\)/);
    return match ? match[1] : '';
  };

  // Extract EPS from description
  const extractEPS = (event: EventCard): string => {
    // First try to extract from description
    if (event.description) {
      // Match patterns like "$-2.38" or "$2.38" in the description
      const epsMatch = event.description.match(/consensus estimate.*?is\s+\$(-?\d+\.?\d*)/i);
      if (epsMatch) {
        return `$${epsMatch[1]} EPS`;
      }
    }

    // Fallback: try to find EPS in market questions
    if (event.top_markets && event.top_markets.length > 0) {
      const epsMatch = event.top_markets[0].question.match(/\$?(-?\d+\.?\d*)\s*EPS/i);
      if (epsMatch) {
        return `$${epsMatch[1]} EPS`;
      }
    }

    // Last fallback
    return '$0.00 EPS';
  };

  // Calculate percentage from outcome prices (Yes price = probability of beating earnings)
  const calculatePercentage = (): number => {
    if (event.top_markets && event.top_markets.length > 0) {
      const yesPrice = event.top_markets[0].outcome_prices[0] || 0;
      return Math.round(yesPrice * 100);
    }
    return 0;
  };

  const ticker = extractTicker(event.title);
  const eps = extractEPS(event);
  const percentage = calculatePercentage();

  // Determine color based on percentage
  const getPercentageColor = (pct: number): string => {
    if (pct >= 70) return '#10b981'; // Green
    if (pct >= 50) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  const percentageColor = getPercentageColor(percentage);

  return (
    <div
      className={`earnings-event-card ${marketTiming}`}
      onClick={() => onCardClick(event)}
    >
      {/* Left: Icon + Text */}
      <div className="earnings-left">
        <div className="earnings-card-icon">
          {event.image ? (
            <img
              src={event.image}
              alt={ticker || event.title}
              className="earnings-icon-img"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="earnings-icon-fallback">
              {ticker.charAt(0) || 'E'}
            </div>
          )}
        </div>
        <div className="earnings-info">
          <div className="earnings-ticker">{ticker || 'N/A'}</div>
          <div className="earnings-eps">{eps}</div>
        </div>
      </div>

      {/* Right: Dot + (Percentage over beats) */}
      <div className="earnings-right">
        <span className="earnings-dot" style={{ backgroundColor: percentageColor }} />
        <div className="earnings-right-text">
          <span className="earnings-percentage-text">{percentage}%</span>
          <span className="earnings-beats">beats</span>
        </div>
      </div>
    </div>
  );
};

export default EarningsEventCard;
